/**
 * `R1.1-F2` · **Las ZONAS de la consola, LEÍDAS de la declaración de instancia.**
 *
 * Una zona es el lugar donde el operador está parado: uno de los brazos que la
 * instancia declara, o las transversales. De la zona salen el sello, la miga, la
 * leyenda de cada pantalla y el texto de cada popup — todos dicen lo mismo y
 * todos salen de acá.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ENMIENDA `[22/9/2026]` · **LOS BRAZOS DEJARON DE SER UNA CONSTANTE DE
 * COMPILACIÓN, Y EL HECHO QUE LO OBLIGA ESTÁ MEDIDO**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ~~Acá decía `BRAZOS = ["hibrido", "puro"] as const` y `type Brazo =
 * (typeof BRAZOS)[number]`, con este descargo: «`PRD_R1.1.md:204` dice que solo
 * existen los brazos `hibrido` y `puro`… un brazo abierto no puede cumplir
 * `CA-91`…`CA-99`».~~ **Se REDACTA y no se borra** (`ADR-0082`): el razonamiento
 * era correcto para el mundo de una sola instancia, y un lector que llegue con
 * él en la cabeza tiene que encontrar acá qué lo levantó.
 *
 * ⭐ **Lo levanta un hecho, no un argumento.** Se desplegó una segunda instancia
 * —`OLI`— con UN solo brazo. Su `configuracion.json` servido declara
 * `"brazos": ["hibrido"]`, el motor la honra y `admin-web` falla CERRADO ante
 * `GET oli-api/puro/participantes` («el primer segmento del camino tampoco es un
 * brazo que esta instancia declare»). **La consola era el único lugar que no
 * honraba la declaración**: dibujaba dos cajas y se rotulaba `KAI`, las dos cosas
 * horneadas en este archivo.
 *
 * ⛔ **La línea que era el corazón del defecto era la del `type`**, no la de la
 * lista: `type Brazo = (typeof BRAZOS)[number]` convierte «qué brazos hay» en un
 * tipo de compilación, **y un tipo no se lee de una variable de entorno**. Por eso
 * `Brazo` pasa a ser `string` y **la validación pasa a ser de ejecución**
 * (`esBrazo`), contra la lista que la instancia declaró.
 *
 * ⚠️ **Lo que esto NO afloja:** `#/brazos/<lo que sea>/…` **sigue** cayendo en
 * «Este brazo no existe» (`CA-91`…`CA-99`), y `Hibrido` con mayúscula **sigue**
 * rechazándose (`CA-36`, `CA-95`). Lo que cambió es **contra qué lista** se
 * compara: antes contra dos literales de este archivo, ahora contra lo que la
 * instancia declaró. Sobre `KAI` —que declara los dos— el comportamiento
 * observable es **idéntico**; sobre `OLI` deja de mentir.
 *
 * ⚠️ **Y `contracts/**` sigue diciendo lo suyo y no se toca desde acá.**
 * `ARM_VALUES = ["puro", "hibrido"]` es la unión cerrada de `ADR-0299`, una
 * decisión del dueño del 16/9 y territorio de otra corrida.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⟦la-declaracion-se-instala-antes-de-montar⟧ · DE DÓNDE SALE, Y POR QUÉ ASÍ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `canonicalizar()` de `rutas.ts` es una **función pura** que corre fuera de React,
 * así que la declaración no puede vivir sólo en un contexto de React: tiene que
 * estar disponible para código que no tiene `hooks`. ⇒ se **instala una vez**, en
 * la costura de arranque que ya existía y ya era limpia: `main.tsx` resuelve
 * `configuracion.cargar()` **antes** de montar nada, justamente para que no haya
 * un estado intermedio en el que la pantalla se ve rota sin decir por qué.
 *
 * ⛔⛔ **SIN DEFAULT, Y ES LA MITAD QUE IMPORTA.** «La instancia no declaró
 * brazos» y «no se pudo leer la configuración» son **dos cosas distintas y no
 * pueden colapsar**:
 *
 *   · **No declarados** ⇒ la lista es `[]`, Inicio no dibuja ninguna caja y toda
 *     dirección de brazo cae en «Este brazo no existe». No es un error: es la
 *     instancia diciendo que no tiene brazos que mostrar.
 *   · **Configuración ilegible** ⇒ `configuracion.ts` lanza y `main.tsx` dibuja
 *     «No se pudo iniciar el Management Tool» con el archivo y lo que falta. Ése
 *     es el camino que ya existía y **se conserva**.
 *
 * Un default `["hibrido", "puro"]` acá colapsaría los dos: la consola de `OLI`
 * volvería a dibujar un brazo que su instancia no declara, que es **exactamente
 * el defecto que este cambio repara**.
 */

/*
 * ⛔ `R3-UI0` `[25/9/2026]` · **EL ÚNICO IMPORT DE ESTE ARCHIVO, y va justificado.**
 * `A15`/`R3-E10` pide que el texto de la capacidad salga de la MISMA lectura que
 * usa el panel de botones (ver §`derivacionDelBrazo`), y esa lectura vive en
 * `configuracion.ts`. Se importa el tipo ANGOSTO (`ConLaDerivacionDeclarada`,
 * un solo campo) y no `ConfiguracionInstancia`, para no derogar lo que
 * §`DeclaracionDeZonas` declara: este módulo no tiene por qué ver la dirección de
 * `admin-web` ni el tamaño de página. `configuracion.ts` no importa de acá, así
 * que no hay ciclo.
 */
import { derivacionHabilitadaDe } from "./configuracion.ts"
import type { ConLaDerivacionDeclarada } from "./configuracion.ts"

/**
 * ⛔ **Un brazo es un `string` opaco**, como `contracts/platform/index.ts:138`
 * dijo siempre (`type Arm = string`). Lo que lo vuelve un brazo **de esta
 * instancia** no es su forma, es estar en la lista declarada — y eso sólo se
 * puede saber en ejecución (`esBrazo`).
 */
export type Brazo = string

/**
 * Lo que la consola necesita saber de la instancia para dibujar sus zonas.
 *
 * ⚠️ Es un subconjunto de `ConfiguracionInstancia` a propósito: este módulo no
 * tiene por qué ver la dirección de `admin-web` ni el tamaño de página.
 */
export interface DeclaracionDeZonas {
  /** El nombre visible de la instancia, **si la declaró**. `PRD_R1.1.md` §2 Glosario. */
  readonly instancia?: string
  /** Los brazos declarados, **en el orden en que se muestran**. */
  readonly brazos?: readonly string[]
  /** Cuál de los brazos declarados deriva a humano, **si la instancia lo declaró**. */
  readonly brazoHibrido?: string
  /**
   * La capacidad por brazo, si la instancia la declaró (`configuracion.ts`
   * §`derivacionPorBrazo`). `main.tsx` instala la configuración ENTERA, así que en
   * producción viaja; la lee `llegadaDeLaDerivacion` cuando falta `brazoHibrido`.
   */
  readonly derivacionPorBrazo?: Readonly<Record<string, boolean>>
}

/**
 * ⛔ Arranca VACÍA y no `null`: leerla antes de instalarla no es un error a
 * gritar, es una instancia que todavía no declaró nada — y el resultado de eso
 * («ningún brazo») es el mismo que el de una instancia que no declara brazos.
 * Un `throw` acá convertiría un orden de importación en una pantalla en blanco.
 */
let declarada: DeclaracionDeZonas = {}

/**
 * Instala la declaración. La llama `main.tsx` **antes de montar** y `App()` en su
 * primer renglón, para que montar `<App config={…} />` suelto —que es lo que hace
 * cada examen— no dependa de haber llamado a esto a mano.
 *
 * ⚠️ Es idempotente y sin estado derivado: volver a llamarla con la misma
 * declaración no cambia nada, que es lo que hace que el doble render de
 * `StrictMode` sea inocuo.
 */
export function instalarLaDeclaracion(declaracion: DeclaracionDeZonas): DeclaracionDeZonas {
  // ⟲ 30/9/2026 · L5: devuelve la ANTERIOR, para que un examen que instala la suya
  // pueda devolverla al terminar (estado de módulo, compartido por el proceso).
  const anterior = declarada
  declarada = declaracion
  return anterior
}

/**
 * Los brazos que la instancia declaró, en su orden. **Vacía si no declaró
 * ninguno**, que es un valor legítimo y no un error. Ver
 * ⟦la-declaracion-se-instala-antes-de-montar⟧.
 */
export function brazos(): readonly string[] {
  return declarada.brazos ?? []
}

/**
 * El nombre visible de la instancia, o `null` si no lo declaró.
 *
 * ⛔ **`null` y no `"KAI"`**: un default acá es lo que hacía que la consola de
 * `OLI` se rotulara con el nombre de otra instancia.
 */
export function instancia(): string | null {
  return declarada.instancia ?? null
}

/**
 * ⛔ **Distingue mayúsculas, y es un requisito**: `R1.1-F1-CA-36` y
 * `R1.1-F2-CA-95` piden que `Hibrido` se rechace. Una comparación con
 * `toLowerCase()` los pondría en rojo a los dos.
 *
 * ⚠️ Y compara contra la lista DECLARADA, no contra dos literales: sobre una
 * instancia que declara un solo brazo, el otro se rechaza igual que cualquier
 * invento — que es todo el punto de este archivo.
 */
export function esBrazo(valor: string | null | undefined): valor is Brazo {
  return valor !== null && valor !== undefined && brazos().includes(valor)
}

/**
 * ⭐ **LA CAPACIDAD, QUE ES LO QUE DECIDE — NUNCA EL NOMBRE.**
 *
 * `docs/prd/PRD_R1.md:194`, textual: *«el comportamiento no se ata al nombre del
 * brazo, se ata a una capacidad declarada. El producto nunca pregunta “¿este
 * brazo se llama Híbrido?”; pregunta “¿este brazo tiene derivación
 * habilitada?”»*. La señal declarativa es `brazoHibrido`, que ya viajaba en
 * `configuracion.json`, ya se validaba sin default (`configuracion.ts`
 * §`brazoHibridoDeclarado`) y **hasta hoy no tenía ni un consumidor**.
 *
 * ⛔⛔ **ESTO EXISTE PARA QUE HAYA UNA SOLA ENTRADA, Y NO ES UNA COMODIDAD.**
 * Había DOS condicionales decidiendo esto, **con polaridades OPUESTAS**:
 * `padron/tabla.tsx` preguntaba `brazo === "hibrido"` (⇒ ante cualquier otro
 * nombre **esconde**) y `metricas/pantalla.tsx` preguntaba `brazo === "puro"`
 * (⇒ ante cualquier otro nombre **muestra**). Sobre una instancia cuyo brazo no
 * se llame como ninguno de los dos literales, las dos pantallas contestan
 * **distinto sobre el mismo brazo**: el Padrón esconde «Pidió humano» y Métricas
 * la muestra. `ADR-0315` lo dice mejor que este comentario: *«Hoy nombre y
 * capacidad coinciden en la instancia vigente, y por eso el defecto NO SE VE. Eso
 * lo hace más peligroso, no menos.»*
 *
 * ⚠️ **La dirección de la falla se conserva, y está elegida:** sin `brazoHibrido`
 * declarado **no se esconde nada**. `configuracion.ts` §`brazoHibrido` lo dejó
 * escrito y la razón no cambió: *«Se prefiere fallar hacia mostrar de más antes
 * que hacia esconder en silencio»* — una columna de más se ve; una métrica que
 * desaparece sola, no.
 *
 * ⚠️ **Y el costo, dicho antes y no después:** con `brazoHibrido` ausente, el
 * brazo sin derivación vuelve a dibujar «Pidió humano», que es el defecto que
 * `App.tsx` §`MetricasDelBrazo` midió en QA. **Por eso el entrypoint ahora lo
 * DERIVA de la declaración** (`infra/docker/management-ui/30-configuracion.sh`,
 * del brazo con `derivacion_habilitada: true`) en vez de esperar que alguien lo
 * escriba: es el mismo remedio que `canales` y `brazos` ya usan, y el mismo
 * motivo (`ADR-0231`: dos listas que tienen que coincidir y se escriben en dos
 * lugares DIVERGEN).
 *
 * ⛔ `brazo === null` —una pantalla montada fuera de una ruta de brazo— también
 * muestra: no hay brazo sobre el que afirmar que le falta la capacidad.
 */
export function derivaAHumano(brazo: Brazo | null): boolean {
  if (brazo === null) return true
  const elHibrido = declarada.brazoHibrido
  if (elHibrido === undefined) return true
  return brazo === elHibrido
}

/**
 * El nombre visible de un brazo.
 *
 * ⛔⛔ **ES UNA ETIQUETA Y NADA MÁS, Y ESO ES LO QUE LA HACE ADMISIBLE.**
 * `PRD_R1.md` `R1-F1 §6` permite exactamente esto y nada más: *«el campo `nombre`
 * es solo etiqueta y **no puede alterar comportamiento**»*. Ninguna decisión de
 * este archivo se toma mirando este mapa — la lista de brazos sale de la
 * declaración (`brazos()`) y la capacidad sale de `derivaAHumano()`.
 *
 * ⭐ El mapa existe porque los criterios piden los nombres **con su tilde**:
 * `R1.1-F1-CA-01` pide «KAI · Brazo Híbrido» y «KAI · Brazo Puro», y de un
 * identificador de ruta sin tildes no hay forma de derivar «Híbrido». Un brazo
 * cuyo identificador no esté acá **se muestra con su identificador**, que es lo
 * único honesto: inventarle un nombre bonito sería decidir por él.
 */
const ETIQUETA_DEL_BRAZO: Readonly<Record<string, string>> = {
  hibrido: "Brazo Híbrido",
  puro: "Brazo Puro",
}

export function nombreDelBrazo(brazo: Brazo): string {
  return ETIQUETA_DEL_BRAZO[brazo] ?? brazo
}

/**
 * ⭐ **El SELLO: «KAI · Brazo Puro».** Es el texto que piden, literal, los
 * criterios `R1.1-F2-CA-32`…`CA-51` (sello y miga), `R1.1-F4-CA-01`, `CA-14`,
 * `CA-20`, `CA-21`, `CA-30`, `CA-89`, y el aviso de cruce `CA-22`.
 *
 * ⛔ Una sola función y no un literal por sitio: el punto medio es `·` (U+00B7) y
 * no un guion; un sitio que lo escriba distinto pasa `tsc` y falla el criterio.
 *
 * ⚠️ **Sin nombre de instancia declarado, el sello es sólo el del brazo** —«Brazo
 * Puro»— y no «undefined · Brazo Puro» ni «KAI · Brazo Puro». Es la misma regla
 * que `instancia()`: no se muestra lo que no se declaró, y no se inventa.
 */
export function selloDelBrazo(brazo: Brazo): string {
  return conLaInstanciaAdelante(nombreDelBrazo(brazo))
}

/** `«<instancia> · <qué>»`, o sólo `«<qué>»` si la instancia no se declaró. */
function conLaInstanciaAdelante(que: string): string {
  const nombre = instancia()
  return nombre === null ? que : `${nombre} · ${que}`
}

/** El sello de las transversales. `R1.1-F3-CA-01` y `CA-02`. */
export function selloTransversal(): string {
  return conLaInstanciaAdelante("Transversales")
}

/**
 * La MIGA de las transversales, que **no dice lo mismo que el sello**.
 * `R1.1-F5-CA-07`, `CA-10`, `CA-13`, `CA-16`, `CA-19`, `CA-22`, `CA-25` y `CA-28`.
 *
 * ⛔⛔ **SON DOS TEXTOS A PROPÓSITO, Y UNIFICARLOS CAMBIA DOS VERDES POR OCHO.**
 * El sello lo fija el PRD (`:509-510`) en «KAI · Transversales» y esos dos
 * criterios están verdes. La miga la fija el boceto, que pide «Funcionalidades
 * transversales». El boceto **no marca diferencia de sello**, sólo de miga: son
 * separables, y hasta hoy compartían una sola constante —que es justamente por lo
 * que los ocho estaban en rojo.
 *
 * ⭐ Antes de cambiarlo se verificó que el PRD no contradijera al boceto, que es
 * lo que el reparto pedía comprobar: **el PRD no tiene ningún criterio sobre el
 * texto de la miga transversal** —los de miga, `F2-CA-42`…`CA-49`, son todos de
 * pantallas del brazo— y llama «Funcionalidades transversales» a esta zona en
 * `:114`, `:299`, `:347` y en el título de `F3` (`:461`). No hay conflicto.
 */
export function migaTransversal(): string {
  return conLaInstanciaAdelante("Funcionalidades transversales")
}

/**
 * Qué hace cada brazo, tal cual lo piden `R1.1-F2-CA-08` y `CA-09`.
 *
 * ⛔ Sale de la CAPACIDAD y no de un mapa por nombre: era el otro sitio donde
 * «qué puede hacer este brazo» estaba escrito al lado de cómo se llama.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔⛔ `A15` / `R3-E10` `[25/9/2026]` · **UNA CAPACIDAD NO DECLARADA SE LEE
 * NEGADA — Y ESTO SE ARREGLA ACÁ Y NO EN `derivaAHumano`**
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `docs/prd/PRD_R3-errata.md` `R3-E10`, textual: *«una capacidad de derivación **no
 * declarada se lee como negada en todas partes**: el panel de botones, Inicio, la
 * rama del toque …»*. Cierra `R2-P5` / `R2-H-026`: hoy la consola **se
 * contradecía sola** —el panel de botones la negaba (`plantillas/botones/pantalla.tsx:324`,
 * por `derivacionHabilitadaDe`) e Inicio la afirmaba (acá, por `derivaAHumano`)—
 * y las dos lecturas vivían a tres archivos de distancia.
 *
 * ⇒ esta función pasa a leer **la MISMA** función que el panel de botones:
 * `configuracion.ts` §`derivacionHabilitadaDe`, que devuelve `boolean | null`.
 * `null` («la instancia no lo declaró») ⇒ **negada**, que es lo que `R3-E10` fija.
 * Sólo `true` dice «Con derivación a humano».
 *
 * ⟦rastro · ADR-0082 · R4-UIT1 · 4/10/2026⟧ acá decía «Con derivación a **tutores**»:
 * `R4-F4` (`docs/prd/PRD_R4.md:1976`) renombra la capacidad a «derivación a humano» y
 * este comentario describe el TEXTO que la función devuelve (`:360`), así que se corrige
 * con el valor nuevo en vez de quedar describiendo como vigente uno que ya no sale.
 *
 * ⛔⛔ **Y `derivaAHumano` NO SE TOCA, que es la mitad que importa.** Poner su
 * default en `false` parece lo mismo y no lo es: esa función decide si **Padrón y
 * Métricas DIBUJAN la columna «Pidió humano»** (`padron/tabla.tsx:266`,
 * `metricas/pantalla.tsx:622`) y falla hacia **mostrar** a propósito —su propio
 * docblock lo declara: *«una columna de más se ve; una métrica que desaparece
 * sola, no»*—. Cambiarla escondería una columna en dos pantallas de otros
 * territorios, **y ningún criterio de `R3-UI0` lo mediría**.
 *
 * ⇒ las dos lecturas conviven y son distintas a propósito, y acá queda dicho cuál
 * es cuál:
 *   · `derivaAHumano(brazo)` — *«¿dibujo el aparato de “Pidió humano”?»*. Falla
 *     hacia **mostrar**. La leen Padrón, Métricas, Nuevo envío y el orden de las
 *     cajas de Inicio.
 *   · `derivacionDelBrazo(config, brazo)` — *«¿qué le DIGO al operador que este
 *     brazo puede hacer?»*. Falla hacia **negar** (`R3-E10`). La leen el sello del
 *     menú y la caja de Inicio.
 *
 * ⚠️ **LO QUE ESTO NO CIERRA, dicho acá:** con `derivacionPorBrazo` ausente el
 * TEXTO queda negado en las dos pantallas, pero la columna «Pidió humano» sigue
 * dibujándose, así que la consola muestra «Sin derivación a humano» al lado de
 * una columna que registra derivaciones. `R3-E10` pide negarla *«en todas
 * partes»*, y *«la rama del toque»* y la ruta de respuestas automáticas son del
 * servidor (R3-F2/R3-F3), no de acá. Unificar las dos lecturas de la consola es
 * una decisión que ningún criterio de esta etapa mide y que tiene dueño: el TL.
 *
 * ⟦rastro · ADR-0082 · R4-UIT1 · 4/10/2026⟧ el texto de este párrafo decía «Sin
 * derivación a **tutores**»; `R4-F4` (`PRD_R4.md:1976`) lo renombra a «derivación a
 * humano», que es lo que la función devuelve hoy (`:361`).
 *
 * ⛔ **Recibe la declaración por PARÁMETRO y no la lee de `declarada`.** La
 * declaración de este archivo es del PROCESO —se instala una vez en `main.tsx` o
 * en `pruebas/preload.ts`, ver ⟦la-declaracion-se-instala-antes-de-montar⟧— y
 * `App.tsx` §`App` tiene medido qué pasa cuando un examen la pisa con su `CONFIG`
 * mínimo: **211 exámenes en rojo**. Pedirla por parámetro deja que cada pantalla
 * la tome del contexto que ya existe (`useConfiguracion()`), que es el mismo
 * objeto que `main.tsx` le pasa a las dos cosas en producción.
 */
export function derivacionDelBrazo(
  declaracion: ConLaDerivacionDeclarada,
  brazo: Brazo,
): string {
  return derivacionHabilitadaDe(declaracion, brazo) === true
    ? "Con derivación a humano"
    : "Sin derivación a humano"
}

/**
 * Los **otros** brazos declarados. Lo necesita el aviso de cruce de Alta manual
 * (`R1.1-F4-CA-22`: «Este teléfono también está en KAI · Brazo Híbrido.»).
 *
 * ⛔⛔ **REEMPLAZA A `elOtroBrazo()`, QUE ERA TOTAL Y NO PODÍA SEGUIR SIÉNDOLO.**
 * Su propio JSDoc ya avisaba: *«Es total porque los brazos son exactamente dos. El
 * día que haya un tercero esta función deja de tener sentido y `tsc` **no lo va a
 * decir**»*. El día llegó por el otro lado —una instancia con UNO—, donde **no hay
 * «el otro»** y aquella función devolvía un brazo que la instancia no declara.
 *
 * ⇒ devuelve una LISTA, que con un brazo es vacía y con dos tiene exactamente el
 * que antes se adivinaba. El sitio que la consume decide qué hacer con el vacío,
 * que es lo que una función total no lo dejaba decidir.
 */
export function losOtrosBrazos(brazo: Brazo): readonly string[] {
  return brazos().filter((otro) => otro !== brazo)
}

/**
 * ⛔⛔⛔ **LOS BRAZOS EN EL ORDEN EN QUE INICIO LOS MUESTRA, Y SIN ESTO
 * `R1.1-F2-CA-03` SE PONE ROJO EN EL AMBIENTE DESPLEGADO.**
 *
 * `CA-03` (`PRD_R1.1.md:521`): *«Los brazos tienen orden fijo: en Inicio la caja
 * “KAI · Brazo Híbrido” está **a la izquierda** de “KAI · Brazo Puro”»*.
 *
 * ⚠️ **Medido corriendo el entrypoint contra la declaración real** (22/9/2026):
 * `configuracion.json` trae `"brazos": ["puro","hibrido"]` — **el Puro PRIMERO**.
 * Sale de `STUDY_ARMS=puro,hibrido` (`qa/entorno/env/comun.env:14`), que es de
 * donde lo derivan tanto `infra/docker/management-ui/30-configuracion.sh` como
 * `qa/entorno/levantar.sh`, y `UI_BRAZOS` **no está declarada en ningún lado** del
 * árbol. ⇒ iterar la declaración en su orden crudo dibujaría el Puro a la
 * izquierda en toda instancia desplegada.
 *
 * ⛔ Hasta hoy no pasaba porque el orden lo fijaba `BRAZOS = ["hibrido", "puro"]`,
 * con el híbrido escrito primero. Leer los brazos de la declaración **sin
 * ordenarlos** cambia un verde por un rojo.
 *
 * ⭐ **La traducción correcta del criterio no es «`hibrido` primero» sino «el
 * brazo que DERIVA primero»** (`PRD_R1.md:194`). Sobre `KAI` las dos frases dan el
 * mismo resultado —que es lo que mantiene `CA-03` verde—; sobre una instancia con
 * otros nombres sólo la segunda significa algo.
 *
 * ⚠️ **`sort` es ESTABLE**, así que los brazos de igual capacidad conservan el
 * orden declarado. Sin `brazoHibrido`, `derivaAHumano` es `true` para todos ⇒ no
 * se mueve ninguno y el orden es, exacto, el de la declaración.
 *
 * ⛔ **DUEÑO: TL.** Esto ORDENA lo que la instancia declaró, y es una decisión de
 * producto que este archivo toma para no introducir una regresión. La alternativa
 * —dejar el orden crudo y corregir `STUDY_ARMS` a `hibrido,puro`— vive en `qa/` e
 * `infra/` y cambiaría el orden para los TRES procesos que leen esa variable.
 */
export function brazosEnOrdenDeInicio(): readonly string[] {
  return [...brazos()].sort((a, b) => Number(derivaAHumano(b)) - Number(derivaAHumano(a)))
}

/**
 * El rótulo de la caja de un brazo en Inicio: `«KAI · Brazo del estudio»`.
 *
 * ⚠️ Vive acá y no en el JSX de `App.tsx` por lo mismo que `selloDelBrazo` y
 * `migaTransversal`: **los textos de zona salen de un solo archivo**, con la misma
 * regla de separador y la misma degradación cuando la instancia no declaró su
 * nombre —sin `·` suelto adelante y sin inventar uno—.
 */
export function rotuloDeLaCajaDelBrazo(): string {
  return conLaInstanciaAdelante("Brazo del estudio")
}

/** Los sellos de todos los brazos declarados, para los textos que los nombran juntos. */
export function sellosDeLosBrazos(): readonly string[] {
  return brazos().map(selloDelBrazo)
}

/**
 * ⭐⭐ **A QUÉ BRAZOS APLICA ALGO DE LA INSTANCIA, DICHO CON LOS BRAZOS
 * DECLARADOS.** `[30/9/2026]` · `fix/consola-literales-de-brazo`.
 *
 * ⛔ Medido en UAT, logueado, sobre `OLI` (declara UN brazo): las transversales
 * decían «Aplica a los dos brazos (Híbrido y Puro)», «Llega a: Brazo Puro» y «los
 * cambios aplican a los dos brazos» — el CONTEO y los NOMBRES de `KAI` escritos en
 * el JSX. `AP40 · ④` no los veía porque sólo acusa una cadena que ES un nombre de
 * brazo; `AP40 · ⑥` (`gates/literales-de-brazo.spec.ts`) es el que los ve ahora.
 *
 * ⇒ las frases salen de acá, armadas con `brazos()`:
 *   · dos ⇒ «los dos brazos» — exactamente lo de `KAI`, que es lo que piden
 *     `R1.1-F3-CA-11`, `CA-62` y el popup del Banco, y lo que sus exámenes miden;
 *   · uno ⇒ «el Brazo Híbrido» (su nombre visible);
 *   · más ⇒ «los N brazos»; ninguno ⇒ «toda la instancia», sin inventar brazos.
 */
export function losBrazos(): string {
  const declarados = brazos()
  const [unico] = declarados
  if (declarados.length === 1 && unico !== undefined) return `el ${rotuloDelBrazo(unico)}`
  if (declarados.length === 0) return "toda la instancia"
  return declarados.length === 2 ? "los dos brazos" : `los ${declarados.length} brazos`
}

/** `losBrazos()` con la preposición «a» adelante, contraída: «al Brazo Híbrido». */
export function aLosBrazos(): string {
  const cuales = losBrazos()
  return cuales.startsWith("el ") ? `al ${cuales.slice(3)}` : `a ${cuales}`
}

/**
 * El nombre visible si hay etiqueta («Brazo Híbrido»), o «brazo <id>» si no: un
 * id pelado («Llega a: tutoria») no dice que es un brazo. ⟲ 30/9/2026 · L8.
 */
function rotuloDelBrazo(brazo: Brazo): string {
  const nombre = nombreDelBrazo(brazo)
  return nombre === brazo ? `brazo ${brazo}` : nombre
}

/** «A», «A y B», «A, B y C». */
export function enumerar(partes: readonly string[]): string {
  if (partes.length <= 1) return partes.join("")
  return `${partes.slice(0, -1).join(", ")} y ${partes[partes.length - 1] ?? ""}`
}

/**
 * Los nombres CORTOS de los brazos declarados —«Híbrido y Puro»—, en el orden de
 * Inicio (el que deriva primero, ver `brazosEnOrdenDeInicio`). Es el paréntesis
 * de `R1.1-F3-CA-11`; con menos de dos brazos no hay nada que aclarar y el sitio
 * que lo usa no lo dibuja.
 *
 * ⚠️ El nombre corto es el visible sin «Brazo »: la etiqueta sigue viviendo en
 * UN solo mapa (`ETIQUETA_DEL_BRAZO`), y un segundo mapa con «Híbrido» sería
 * otra copia del nombre que `AP40 · ④` acusaría con razón.
 */
export function nombresDeLosBrazos(): string {
  return enumerar(brazosEnOrdenDeInicio().map((b) => nombreDelBrazo(b).replace(/^Brazo /, "")))
}

/**
 * ⭐ «Llega a: …» de las dos tarjetas de derivación (`R1.1-F3-CA-78`/`CA-79`).
 *
 * ⛔ Antes eran dos literales —«Llega a: Brazo Puro» y «Llega a: Brazo Híbrido»—
 * que sobre `OLI` le anunciaban el texto «sin tutores» a un brazo que no existe.
 * Ahora cada tarjeta nombra los brazos DECLARADOS que tienen (o no) la capacidad,
 * leída de `brazoHibrido` —que el entrypoint deriva del brazo con
 * `derivacion_habilitada: true` (`infra/docker/management-ui/30-configuracion.sh`),
 * la misma señal que `derivaAHumano`—, nunca del nombre.
 *
 * ⚠️ Si ningún brazo declarado cae de ese lado, lo dice («ningún brazo de esta
 * instancia») en vez de callar: la tarjeta sigue ahí y su texto se sigue
 * guardando. Sin `brazoHibrido` declarado no hay de dónde saber cuál deriva, y se
 * nombra la capacidad en vez de un brazo.
 */
export function llegadaDeLaDerivacion(deriva: boolean): string {
  const elHibrido = declarada.brazoHibrido
  const tabla = declarada.derivacionPorBrazo
  /*
   * ⟲ 30/9/2026 · M2 (revisión): sin `brazoHibrido` se lee `derivacionPorBrazo`
   * —la tabla que ya usa `derivacionDelBrazo`— ANTES de caer al texto genérico. Un
   * brazo que la tabla no nombra se lee NEGADO, como fija `R3-E10`. Sólo sin
   * ninguna de las dos señales se nombra la capacidad en vez de un brazo.
   */
  const tieneDerivacion: ((b: Brazo) => boolean) | null =
    elHibrido !== undefined
      ? (b) => b === elHibrido
      : tabla !== undefined
        ? (b) => derivacionHabilitadaDe(declarada, b) === true
        : null
  if (tieneDerivacion === null) {
    return deriva ? "Llega a: el brazo con derivación" : "Llega a: el brazo sin derivación"
  }
  const cuales = brazosEnOrdenDeInicio().filter((b) => tieneDerivacion(b) === deriva)
  return cuales.length === 0
    ? "Llega a: ningún brazo de esta instancia"
    : `Llega a: ${enumerar(cuales.map(rotuloDelBrazo))}`
}
